<script setup lang="ts">
import { type PropType, onMounted, onUnmounted, ref } from 'vue';
import type { ResetPasswordSecondStepData } from '../types/steps';
import BaseFormGroup from '~/shared/components/form/base-form-group.vue';
import BaseInput from '~/shared/components/form/input/base-input.vue';

const props = defineProps({
  codeErrors: {
    type: Object as PropType<{ verificationCode: string }>,
    required: true,
  },
  codeSentAt: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits<{
  (e: 'resend:code'): void
}>();

const data = defineModel({
  type: Object as PropType<ResetPasswordSecondStepData>,
  required: true,
});

const error = ref('');

const codeSendAgainAtDelay = 30; // 30 seconds

function calcDiff() {
  return (Date.now() - props.codeSentAt) / 1000;
}

const diff = ref(calcDiff());

let interval: ReturnType<typeof setInterval> | null = null;

onMounted(() => {
  interval = setInterval(() => {
    diff.value = calcDiff();
  }, 1000);
});

onUnmounted(() => {
  if (interval) {
    clearInterval(interval);
  }
});
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="bg-gray-light border border-stroke-primary p-4 c-text-secondary w-80 rounded-lg">
      Enter the code from your email.
    </div>

    <div class="flex flex-col gap-5.5">
      <div class="flex flex-col gap-4 w-80 relative">
        <span v-if="error" class="absolute top--9 left-0 c-error-pure">
          {{ error }}
        </span>
        <BaseFormGroup name="verification-code" :error="codeErrors?.verificationCode || ''" label="Verification Code">
          <BaseInput
            id="verification-code"
            v-model="data.verificationCode"
            name="verification-code"
            placeholder="Enter code here"
            :error="codeErrors?.verificationCode || ''"
          />
        </BaseFormGroup>
      </div>

      <div class="flex justify-between">
        <span>
          Did not get the code?
        </span>

        <span v-if="diff <= codeSendAgainAtDelay" class="c-text-secondary underline font-600">
          Send again in {{ (codeSendAgainAtDelay - diff).toFixed(0) }}s
        </span>

        <button
          v-if="diff > codeSendAgainAtDelay"
          class="c-text-dark hover:c-green-pure transition underline font-600"
          @click="emit('resend:code')"
        >
          Send again
        </button>
      </div>
    </div>
  </div>
</template>
